/**
 * V3.4: Draggable field chip component for pivot builder
 * V3.5: Accessible icon button, SVG icons, label truncation
 */

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useVisuallyHidden } from 'react-aria'
import type { GroupBySource } from '../../types'
import { IconButton } from '../IconButton'

export interface FieldItem {
	id: string
	field: string
	source: GroupBySource
	label: string
	type: 'row' | 'column' | 'value' | 'filter' | 'available'
}

interface DraggableFieldProps {
	item: FieldItem
	onRemove?: () => void
}

function SourceIcon({ source }: { source: GroupBySource }) {
	const { visuallyHiddenProps } = useVisuallyHidden()
	const label = source === 'system_field' ? 'System field' : 'Custom field'

	if (source === 'system_field') {
		return (
			<>
				<svg className='w-3 h-3' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						strokeWidth={2}
						d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
					/>
					<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
				</svg>
				<span {...visuallyHiddenProps}>{label}</span>
			</>
		)
	}

	return (
		<>
			<svg className='w-3 h-3' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
				<path
					strokeLinecap='round'
					strokeLinejoin='round'
					strokeWidth={2}
					d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
				/>
			</svg>
			<span {...visuallyHiddenProps}>{label}</span>
		</>
	)
}

export function DraggableField({ item, onRemove }: DraggableFieldProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: item.id,
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	}

	const typeColors = {
		available: 'bg-neutral-100 border-neutral-300 text-neutral-700',
		row: 'bg-blue-100 border-blue-300 text-blue-700',
		column: 'bg-purple-100 border-purple-300 text-purple-700',
		value: 'bg-green-100 border-green-300 text-green-700',
		filter: 'bg-amber-100 border-amber-300 text-amber-700',
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			className={`
        inline-flex items-center gap-1.5 px-2 py-1 rounded border cursor-grab
        text-xs font-medium transition-all
        ${typeColors[item.type]}
        ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : 'hover:shadow-sm'}
      `}>
			<SourceIcon source={item.source} />
			<span className='max-w-[120px] truncate' title={item.label}>
				{item.label}
			</span>
			{onRemove && item.type !== 'available' && (
				<IconButton
					label={`Remove ${item.label}`}
					onPress={onRemove}
					className='ml-1 p-0.5 rounded hover:bg-black/10 transition-colors'>
					<svg className='w-3 h-3' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
						<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
					</svg>
				</IconButton>
			)}
		</div>
	)
}
